import { Badge } from '@client/components/ui/badge';
import { Quote } from '@client/model';
import { Sparkles } from 'lucide-react';

import type {
  PaymentOptionGroup
} from '@client/hooks/useLtcPlanningQuotesByPaymentOption';
import { LtcPlanningQuoteCard } from './LtcPlanningQuoteCard';

interface PaymentOptionSectionProps {
  group: PaymentOptionGroup;
  expandedQuoteId: string | null;
  onToggleExpand: (quoteId: string) => void;
  onSelectQuote: (quote: Quote) => void;
  testIdPrefix?: string;
}

const PAYMENT_OPTION_DESCRIPTIONS: Record<string, string> = {
  'Single Pay': 'One-time lump sum payment for lifetime coverage',
  '10-Pay': 'Pay over 10 years, then coverage continues for life',
  Monthly: 'Affordable monthly premiums spread over time',
  Annually: 'Annual premium payments for ongoing coverage',
};

function PredictionBadge() {
    return (
      <Badge
        variant="default"
        className="bg-primary text-white border-0 gap-1.5 px-3 py-1"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Best Match
      </Badge>
    );

}

export function PaymentOptionSection({
  group,
  expandedQuoteId,
  onToggleExpand,
  onSelectQuote,
  testIdPrefix = 'payment-option-section',
}: PaymentOptionSectionProps) {
  const { paymentOption, quotes, isPredicted } = group;

  if (quotes.length === 0) {
    return null;
  }

  return (
    <section data-testid={`${testIdPrefix}-${paymentOption.toLowerCase().replace(' ', '-')}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{paymentOption}</h2>
            {isPredicted && <PredictionBadge />}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {PAYMENT_OPTION_DESCRIPTIONS[paymentOption]}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {quotes.map((quote, index) => (
          <LtcPlanningQuoteCard
            key={quote.insuranceQuoteId}
            quote={quote}
            isExpanded={expandedQuoteId === quote.insuranceQuoteId}
            onToggleExpand={() => onToggleExpand(quote.insuranceQuoteId)}
            onSelectQuote={onSelectQuote}
            hasHighestROI={index === 0}
            testId={`${testIdPrefix}-quote-${index}`}
          />
        ))}
      </div>
    </section>
  );
}

